// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { getDateFnsLocale, getIntlLocale } from '../i18n/i18n';

const DETAIL_DATE_TIME_FORMAT = 'd MMM, yyyy h:mm';

/**
 * Safely converts timestamp-like values (Firestore Timestamp, Date, ISO string)
 * into a Date instance.
 *
 * @param {any} value - Timestamp-like value.
 * @returns {Date|null} Valid Date instance or null.
 */
export function toDate(value) {
  if (!value) return null;
  const dateValue = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(dateValue?.getTime?.()) ? null : dateValue;
}

/**
 * Whole seconds elapsed since a start timestamp — how long a stamp has been running.
 *
 * Accepts anything `toDate` accepts, because a stamp's start reaches the UI both as a Firestore
 * Timestamp (saved document) and as a Date (optimistic local state). Returns null rather than 0
 * when the start is unknown, so an unmeasurable duration is never logged as no time at all.
 *
 * @param {any} startTimestamp - Timestamp-like start value.
 * @param {Date} [now=new Date()] - Reference point.
 * @returns {number|null} Elapsed whole seconds, never negative, or null when the start is unusable.
 */
export function getElapsedSeconds(startTimestamp, now = new Date()) {
  const start = toDate(startTimestamp);
  if (!start) return null;

  const reference = toDate(now);
  if (!reference) return null;

  return Math.max(0, Math.round((reference.getTime() - start.getTime()) / 1000));
}

/**
 * Formats a duration from a start timestamp to now
 * @param {Date} startTimestamp - The start timestamp
 * @returns {string} Formatted duration string in the format "XXmin XXs" or "XXhr XXmin XXs"
 */
export function formatDuration(startTimestamp) {
  if (!startTimestamp) return '';

  // Calculate duration in seconds
  const now = new Date();
  const durationMs = now - startTimestamp;
  const durationSec = Math.floor(durationMs / 1000);

  // Calculate hours, minutes, and seconds
  const hours = Math.floor(durationSec / 3600);
  const minutes = Math.floor((durationSec % 3600) / 60);
  const seconds = durationSec % 60;

  // Format the duration string
  if (hours > 0) {
    return `${hours}hr ${minutes}min ${seconds}s`;
  } else {
    return `${minutes}min ${seconds}s`;
  }
}

/**
 * @param {number} value
 * @returns {string}
 */
function padTimeUnit(value) {
  const TIME_UNIT_PAD_LENGTH = 2;
  return String(value).padStart(TIME_UNIT_PAD_LENGTH, '0');
}

/**
 * Formats a duration from a start timestamp to now as a colon-separated clock string.
 * @param {Date} startTimestamp - The start timestamp
 * @param {{ includeHours?: boolean }} [options] - When true, always uses "hh : mm : ss"; otherwise hours are shown only when > 0
 * @returns {string} Formatted duration string as "mm : ss" or "hh : mm : ss"
 */
export function formatDurationClock(startTimestamp) {
  if (!startTimestamp) return '';

  const now = new Date();
  const durationSec = Math.floor((now - startTimestamp) / 1000);
  const hours = Math.floor(durationSec / 3600);
  const minutes = Math.floor((durationSec % 3600) / 60);
  const seconds = durationSec % 60;

  const paddedMinutes = padTimeUnit(minutes);
  const paddedSeconds = padTimeUnit(seconds);

  if (hours > 0) {
    return `${padTimeUnit(hours)} : ${paddedMinutes} : ${paddedSeconds}`;
  } else {
    return `${paddedMinutes} : ${paddedSeconds}`;
  }
}

/**
 * Formats a date object to a human-readable short string.
 *
 * @param {Date|null} date - Date to format
 * @param {string} [languageCode] - App language code (e.g. 'de', 'nl'); its locale comes from i18n, unknown codes format as English.
 * @returns {string} Formatted date string (e.g., "Jan 15, 2024") or empty string if invalid
 */
export function formatDate(date, languageCode) {
  if (!date) return '';
  try {
    return date.toLocaleDateString(getIntlLocale(languageCode), { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}

/**
 * Formats a date object to a localized time string.
 *
 * @param {Date|null} date - Date to format
 * @param {string} [languageCode] - App language code (e.g. 'de', 'nl'); its locale comes from i18n, unknown codes format as English.
 * @returns {string} Formatted time string (e.g., "14:30") or empty string if invalid
 */
export function formatTime(date, languageCode) {
  if (!date) return '';
  try {
    return date.toLocaleTimeString(getIntlLocale(languageCode), { hour: '2-digit', minute: '2-digit' });
  } catch (error) {
    console.error('Error formatting time:', error);
    return '';
  }
}

/**
 * Formats a date object to a localized date and time string.
 *
 * @param {Date|null} date - Date to format
 * @param {string} [languageCode] - App language code (e.g. 'de', 'nl'); its locale comes from i18n, unknown codes format as English.
 * @returns {string} Formatted date-time string or empty string if invalid
 */
export function formatDateTime(date, languageCode) {
  const formattedDate = formatDate(date, languageCode);
  const formattedTime = formatTime(date, languageCode);
  if (!formattedDate || !formattedTime) return '';
  return `${formattedDate} ${formattedTime}`;
}

/**
 * Formats a date-time as "17 Mar, 2026 12:17PM" (day, short month, year, 12-hour clock).
 *
 * @param {Date|null} date - Date to format
 * @param {string} [languageCode] - App language code (e.g. 'de', 'nl'). Defaults to English.
 * @returns {string} Formatted date-time string or empty string if invalid
 */
export function formatDetailDateTime(date, languageCode) {
  if (!date) return '';
  try {
    const dateFnsLocale = getDateFnsLocale(languageCode);
    const dateTimePart = format(date, DETAIL_DATE_TIME_FORMAT, { locale: dateFnsLocale });
    const period = format(date, 'a', { locale: enUS }).toUpperCase();
    return `${dateTimePart}${period}`;
  } catch (error) {
    console.error('Error formatting detail date-time:', error);
    return '';
  }
}

/**
 * Formats minutes into a compact duration label.
 *
 * @param {number} totalMinutes - Duration in minutes
 * @returns {string} Formatted duration label (e.g., "1h 35m")
 */
export function formatHoursMinutesLabel(totalMinutes) {
  const safeMinutes = Math.max(0, Number(totalMinutes) || 0);
  const hours = Math.floor(safeMinutes / 60);
  const minutes = safeMinutes % 60;
  return `${hours}h ${minutes}m`;
}
